import React, { Fragment } from 'react'
import {
  Grid, Typography, Divider, TextField, Box, Button, Accordion, AccordionSummary, AccordionDetails, makeStyles, Hidden
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  Save, Add, Delete, KeyboardArrowDown
} from '@material-ui/icons'
import i18n from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import Imageviewer from '../imageviewer'
import 'react-dropzone-component/styles/filepicker.css'
import 'dropzone/dist/min/dropzone.min.css'
import { mapByIds, useAuth, useFetch } from '../context/useFetch'
import {
  useRemove, useUpdate, useAlert, prependAction, useChangeHandler
} from '../reducer'
import { DropzoneDocs } from '../utils/DropzoneDocs'
import { API_HOST } from '../../src/constants'
import { MonthPicker } from '../utils/MonthPicker'

const useStyles = makeStyles((theme) => ({
  img: {
    width: '60%',
    [theme.breakpoints.down('sm')]: {
      width: '80%'
    }
  },
  imageViewer: {
    position: 'relative',
    '& .onlyIcon': {
      zIndex: '1',
      position: 'absolute',
      right: theme.spacing(0.5),
      top: theme.spacing(0.5)
    }
  }
}))

const initialProject = () => ({
  id: uuidv4(),
  label: '',
  from: '',
  to: '',
  description: '',
  pictures: [],
  technology_ids: [],
  temporary_id: true
})

export default function Portfolio() {
  return useFetch(
    ({ fetch }) => ({
      technologies: fetch('technologies').then((results) => mapByIds(results.data)),
      projects: fetch('profile/projects').then((results) => ({ data: mapByIds(results.data), included: results.included }))
    }),
    () => {
      const projects = useSelector((state) => state.projects)

      const dispatch = useDispatch()
      const prepend = prependAction(dispatch)
      const update = useUpdate()

      const addProject = () => {
        const newProject = initialProject()
        update(['projects', 'data', newProject.id], newProject)
        update(['currentProject'], newProject.id)
      }

      const classes = useStyles()
      return (
        <Fragment>
          <Typography variant="h2" component="h1">
            <Trans i18nKey="profile.candidat.portfolio.title" />
          </Typography>

          <Grid className="profileSection">
            {Object.keys(projects.data).length === 0 ? (
              <Grid container justifyContent="center">
                <img className={classes.img} src="/portfolio.png" />
                <Grid item sm={12} xs={12}>
                  <Box my={2}>
                    <Typography align="center" variant="h1">
                      <Trans i18nKey="profile.candidat.portfolio.empty_slate.body1" />
                    </Typography>
                    <Typography align="center">
                      <Trans i18nKey="profile.candidat.portfolio.empty_slate.body2" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              Object.values(projects.data).map((project, i) => <Project id={project.id} divide={i < Object.keys(projects).length - 1} />)
            )}

            <Box align="right" mt={2}>
              <Button endIcon={<Add />} color="primary" onClick={addProject}>
                <Trans i18nKey="profile.candidat.portfolio.add_project_button" />
              </Button>
            </Box>
          </Grid>

        </Fragment>
      )
    }
  )
}

export const Project = ({ id, divide }) => {
  const technologies = useSelector((state) => (state ? state.technologies : {}))
  const ref = React.createRef()
  const currentProject = useSelector((state) => state.currentProject)
  const classes = useStyles()
  const auth = useAuth()

  const update = useUpdate()
  const remove = useRemove()
  const alert = useAlert()
  const project = useSelector((state) => state.projects.data[id])
  const included = useSelector((state) => state.projects.included.project_pictures)
  const path = ['projects', 'data', id]
  const handler = useChangeHandler(project, ...path)

  const deleteHandler = async () => {
    await (project.id ? auth.deleteFetch(`profile/projects/${project.id}`) : Promise.resolve())
    remove(path)
  }

  const saveHandler = (e) => {
    e.preventDefault()
    auth
      .fetch(`profile/projects/${project.temporary_id ? '' : project.id}`, { method: project.temporary_id ? 'POST' : 'PATCH', body: { project } })
      .then((project) => {
        console.log(project)
        update(['currentProject'], null)

        auth.fetch('profile/projects').then((projects) => {
          update(['projects'], ({ data: mapByIds(projects.data), included: projects.included }))
          alert('notice', i18n.t('alerts.project_saved'))
        })
      })
      .catch(() => alert('error', i18n.t('alerts.error')))
  }

  const pictureDeleteHandler = (projectId, pictureId) => (e) => {
    auth
      .deleteFetch(`profile/project_pictures/${pictureId}?project_id=${projectId}`)
      .then(() => {
        update(
          [...path, 'pictures'],
          project.pictures.filter(({ id }) => id !== pictureId)
        )
        alert('notice', i18n.t('alerts.image_deleted'))
      })
      .catch(() => alert('error', i18n.t('alerts.error')))
  }

  const reloadProjectHandler = () => {
    project.id && auth.fetch('profile/projects').then((results) => ({ data: mapByIds(results.data), included: results.included }))
  }
  const { t } = useTranslation()

  const isOpen = currentProject === project.id

  return (
    <Fragment>
      <form onSubmit={saveHandler}>
        <Accordion
          ref={ref}
          expanded={isOpen}
          onChange={() => update(['currentProject'], isOpen ? null : id)}
          className="profileEdition"
        >
          <AccordionSummary
            id={`panel-header-${project.id}`}
            expandIcon={<KeyboardArrowDown />}
            aria-controls={`panel-content-${project.id}`}
            className="profileEdition"
          >
            <div className="summaryHeader">
              <Typography component="h3" variant="h4">
                {project.label ? project.label : i18n.t('profile.candidat.portfolio.project_title')}
              </Typography>
              <Typography component="span" color="textSecondary">
                {(project.technology_ids || []).map((technology) => (
                  // Retourner la valeur si vide
                  technologies[technology].label ? technologies[technology].label : i18n.t('job_offer.new_job_offer.job.techno')
                ))}
              </Typography>
            </div>
            <Typography component="date" color="textSecondary">
              {moment(project.from ? project.from : undefined).format('YYYY')}
              {' - '}
              {moment(project.to ? project.to : undefined).format('YYYY')}
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            className="profileEdition"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  id="portfolio-project-title"
                  fullWidth
                  label={i18n.t('profile.candidat.portfolio.project_title')}
                  placeholder={i18n.t('profile.candidat.portfolio.exemple_placeholder')}
                  required
                  variant="filled"
                  InputProps={{ disableUnderline: true }}
                  {...handler.default('label')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  id="portfolio-project-title"
                  variant="filled"
                  label={i18n.t('profile.candidat.portfolio.url')}
                  placeholder="https://"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  {...handler.default('url')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MonthPicker
                  id="portfolio-date-start"
                  label={i18n.t('profile.candidat.portfolio.date.from')}
                  {...handler.default('from')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MonthPicker
                  id="portfolio-date-start"
                  label={i18n.t('profile.candidat.portfolio.date.to')}
                  {...handler.default('to')}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="portfolio-technologies"
                  options={Object.values(technologies)}
                  getOptionLabel={(option) => option.label}
                  limitTags={10}
                  noOptionsText={i18n.t('noOptionsText')}
                  renderInput={(params) => (
                    <TextField
                      variant="filled"
                      label={i18n.t('job_offer.new_job_offer.job.techno')}
                      placeholder={i18n.t('job_offer.new_job_offer.job.techno_place_holder')}
                      {...params}
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />
                  )}
                  required
                  popupIcon={<KeyboardArrowDown />}
                  {...handler.multipleComplete('id', technologies)('technology_ids')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  id="portfolio-description"
                  variant="filled"
                  label={i18n.t('profile.candidat.portfolio.description')}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  rowsMax={9}
                  InputProps={{ disableUnderline: true }}
                  {...handler.default('description')}
                />
              </Grid>

              {project.pictures.length !== 0 && (
                <Grid item xs={12}>
                  <div className="likeMuiFormControl">
                    <span className="likeLabel">
                      <Trans i18nKey="profile.candidat.portfolio.screenshot" />
                    </span>
                    <Grid container spacing={3}>
                      {project.pictures.map(({ id }) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} className={classes.imageViewer}>
                          <Button variant="contained" size="small" startIcon={<Delete />} className="onlyIcon" onClick={pictureDeleteHandler(project.id, id)}>
                            <Hidden xsUp>{i18n.t('profile.candidat.portfolio.delete')}</Hidden>
                          </Button>
                          <Imageviewer id={id} isPortfolio />
                          <Button onClick={pictureDeleteHandler(project.id, id)}>{i18n.t('profile.candidat.portfolio.delete')}</Button>
                        </Grid>
                      ))}
                    </Grid>
                  </div>
                </Grid>
              )}

              <Grid item xs={12}>
                <DropzoneDocs
                  likeLabel={i18n.t('profile.candidat.portfolio.dropzone')}
                  postUrl={`//${API_HOST()}/api/profile/project_pictures`}
                  dictDefaultMessage={i18n.t('profile.candidat.portfolio.dropzone_message')}
                  acceptedFiles='image/*'
                  params={{ [`${project.temporary_id ? 'temporary_' : ''}project_id`]: project.id }}
                  eventHandlers={{ success: reloadProjectHandler }}
                />
              </Grid>

              <Grid item xs={12} my={3}>
                <Divider />
              </Grid>

              <Grid item xs={12} container justifyContent="space-between">
                <Button id="portfolio-delete" variant="outlined" size="small" startIcon={<Delete />} disableFocusRipple onClick={() => confirm('Êtes-vous sûr de vouloir supprimer ce projet ?') && deleteHandler()}>
                  {i18n.t('profile.candidat.portfolio.delete')}
                </Button>
                <Button id="portfolio-save" variant="contained" size="small" startIcon={<Save />} color="primary" onClick={saveHandler}>
                  {i18n.t('profile.candidat.portfolio.save')}
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </form>

      {divide &&
        <Box my={2}>
          <Divider />
        </Box>
      }
    </Fragment>
  )
}


